import React, { useEffect, useState } from 'react';

import { Button, ChoiceTile, InputTextArea, Text } from '@spark';

import { submitWaitlistAnswers } from '../api/waitlistApi';
import { clearWaitlistDraft, loadWaitlistDraft, saveWaitlistDraft } from '../utils/waitlistDraft';
import {
    WAITLIST_REASON_STEP,
    WAITLIST_STEPS,
    WAITLIST_STEP_COUNT,
    emptyWaitlistAnswers,
    normalizeWaitlistReason,
    splitIntoColumns,
} from '../utils/waitlistOptions';
import { WaitlistLogo } from './WaitlistLayout';

export const WAITLIST_WIZARD_INTRO =
    'Want to fast-track your access? Detailed applications help us moving your request to the top of the queue.';

/** Where the wizard opens: the saved draft when there is one, else step 1 with `initialAnswers`. */
const initialWizardState = (draftKey, initialAnswers) => {
    const draft = loadWaitlistDraft(draftKey);
    return {
        step: draft?.step ?? 0,
        answers: { ...emptyWaitlistAnswers(), ...(initialAnswers || {}), ...(draft?.answers || {}) },
    };
};

/**
 * Four-step optional questionnaire (Figma MVP-DESIGNES 9141:2939 .. 9144:3246)
 * shown to a signed-in applicant whose request is still pending.
 *
 * Steps 1-3 are single-select tile grids: `Skip` clears the current answer
 * and advances, `Next` advances keeping whatever is selected (Figma renders
 * it enabled even with no choice) and `Back` keeps the selections of later
 * steps.  Step 4 ("Why should we pick you? (Optional)") is a free-text area
 * with only Back and `Done`: Done posts all answers — skipped tiles and a
 * blank text as `null` — then hands the stored request to `onCompleted`.
 *
 * With a `draftKey` (the applicant's email) the step and answers are also
 * kept in a browser-local draft (`utils/waitlistDraft.js`): a refresh on
 * 3/4 reopens on 3/4, Back still shows the earlier choices, and the draft is
 * removed once Done succeeds.  Without a key the wizard is purely in-memory.
 */
export default function WaitlistWizard({ onCompleted, initialAnswers = null, draftKey = null }) {
    const [initialState] = useState(() => initialWizardState(draftKey, initialAnswers));
    const [step, setStep] = useState(initialState.step);
    const [answers, setAnswers] = useState(initialState.answers);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        saveWaitlistDraft(draftKey, { step, answers });
    }, [draftKey, step, answers]);

    const current = WAITLIST_STEPS[step];
    const isLast = step === WAITLIST_STEP_COUNT - 1;
    const isReasonStep = current.key === WAITLIST_REASON_STEP.key;
    const questionId = `waitlist-question-${current.key}`;

    const choose = (option) => setAnswers((prev) => ({ ...prev, [current.key]: option }));

    const goNext = () => setStep((prev) => Math.min(prev + 1, WAITLIST_STEP_COUNT - 1));
    const goBack = () => setStep((prev) => Math.max(prev - 1, 0));

    const skip = () => {
        setAnswers((prev) => ({ ...prev, [current.key]: null }));
        goNext();
    };

    const finish = async () => {
        if (submitting) return;
        setSubmitting(true);
        setError(null);
        const payload = { ...answers, reason: normalizeWaitlistReason(answers.reason) };
        const result = await submitWaitlistAnswers(payload);
        setSubmitting(false);
        if (!result.ok) {
            setError(result.error);
            return;
        }
        clearWaitlistDraft(draftKey);
        onCompleted?.(result.data?.request ?? null, payload);
    };

    const renderColumn = (options) => (
        <div className="waitlist-wizard-column">
            {options.map((option) => (
                <ChoiceTile
                    key={option}
                    label={option}
                    selected={answers[current.key] === option}
                    disabled={submitting}
                    onClick={() => choose(option)}
                />
            ))}
        </div>
    );

    const renderChoices = () => {
        const [leftColumn, rightColumn] = splitIntoColumns(current.options);
        return (
            <div className="waitlist-wizard-grid" role="radiogroup" aria-labelledby={questionId}>
                {renderColumn(leftColumn)}
                {renderColumn(rightColumn)}
            </div>
        );
    };

    // Figma 9144:3246: a bare text area (the question above is its label), no
    // counter; the backend's free-text cap is enforced by `maxLength`.
    const renderReason = () => (
        <InputTextArea
            name="reason"
            className="waitlist-wizard-reason"
            showLabel={false}
            label={WAITLIST_REASON_STEP.question}
            aria-labelledby={questionId}
            placeholder={WAITLIST_REASON_STEP.placeholder}
            maxLength={WAITLIST_REASON_STEP.maxLength}
            value={answers.reason ?? ''}
            disabled={submitting}
            stretch
            onChange={(event) => choose(event.target.value)}
        />
    );

    return (
        <>
            <WaitlistLogo size="small" />
            <Text as="p" variant="lead-xl-regular" color="primary" className="waitlist-wizard-intro">
                {WAITLIST_WIZARD_INTRO}
            </Text>
            <div className="waitlist-wizard-question">
                <div className="waitlist-wizard-question-row">
                    <Text as="h2" id={questionId} variant="heading-5-medium" color="primary">
                        {current.question}
                    </Text>
                    <Text variant="heading-5-medium" color="primary" aria-label={`Step ${step + 1} of ${WAITLIST_STEP_COUNT}`}>
                        {`${step + 1}/${WAITLIST_STEP_COUNT}`}
                    </Text>
                </div>
                {isReasonStep ? renderReason() : renderChoices()}
            </div>
            {error ? (
                <Text as="p" variant="base-regular" role="alert" className="waitlist-wizard-error">
                    {error}
                </Text>
            ) : null}
            <div className="waitlist-wizard-footer">
                {step > 0 ? (
                    <Button
                        size="large"
                        variant="tertiary"
                        className="waitlist-wizard-footer-back"
                        disabled={submitting}
                        onClick={goBack}
                    >
                        Back
                    </Button>
                ) : null}
                <div className="waitlist-wizard-footer-actions">
                    {isLast ? (
                        <Button size="large" variant="primary" disabled={submitting} onClick={finish}>
                            Done
                        </Button>
                    ) : (
                        <>
                            <Button size="large" variant="secondary" onClick={skip}>
                                Skip
                            </Button>
                            <Button size="large" variant="primary" onClick={goNext}>
                                Next
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}
